/* eslint-disable @typescript-eslint/no-use-before-define */
import { APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_COMMANDS } from 'cpool-constants';

import type { SchemaPersonal, SchemaPersonalApplicant } from '../schemas/personal.schema';

type Props = {
    oldLead: SchemaPersonal;
    newLead: SchemaPersonal;
    applicantId1: string;
    applicantId2?: string;
    ApplicantIdsForDeletingNotes?: Array<string>;
};

export default function generatePersonalDataCommands({
    applicantId1,
    applicantId2,
    ApplicantIdsForDeletingNotes,
    newLead,
    oldLead,
}: Props) {
    const commands: Array<{ commandName: string; payload: Record<string, unknown> }> = [];

    commands.push(...createCommandsForApplicantInfo(oldLead.antragsteller1, newLead.antragsteller1, applicantId1));

    if (oldLead.antragsteller2 && newLead.antragsteller2 && applicantId2) {
        commands.push(...createCommandsForApplicantInfo(oldLead.antragsteller2, newLead.antragsteller2, applicantId2));
    }

    const bankField = (v: string | null | undefined) => v ?? null;
    // the form defaults an unset account holder, so a missing one in the lead is no change
    const oldAccountHolder =
        oldLead.haushalt.kontoverbindung?.gehoertZuAntragsteller ?? APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1;
    if (
        bankField(oldLead.haushalt.kontoverbindung?.iban) !== bankField(newLead.haushalt.kontoverbindung?.iban) ||
        bankField(oldLead.haushalt.kontoverbindung?.bic) !== bankField(newLead.haushalt.kontoverbindung?.bic) ||
        bankField(oldLead.haushalt.kontoverbindung?.kreditinstitut) !==
            bankField(newLead.haushalt.kontoverbindung?.kreditinstitut) ||
        oldAccountHolder !== bankField(newLead.haushalt.kontoverbindung?.gehoertZuAntragsteller)
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeBank,
            payload: {
                bic: newLead.haushalt.kontoverbindung.bic,
                gehoertZuAntragsteller: newLead.haushalt.kontoverbindung.gehoertZuAntragsteller,
                iban: newLead.haushalt.kontoverbindung.iban,
                kreditinstitut: newLead.haushalt.kontoverbindung.kreditinstitut,
            },
        });
    }

    if (ApplicantIdsForDeletingNotes && ApplicantIdsForDeletingNotes.length > 0) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.deleteApplicantNotes,
            payload: {
                applicantIds: ApplicantIdsForDeletingNotes,
            },
        });
    }

    return commands;
}

function createCommandsForApplicantInfo(
    oldLead: SchemaPersonalApplicant,
    newLead: SchemaPersonalApplicant,
    applicantId: string,
) {
    const commands: Array<{ commandName: string; payload: Record<string, unknown> }> = [];
    const changedApplicantNames: Partial<SchemaPersonalApplicant['personendaten']> = {};

    if (oldLead.personendaten.anrede !== newLead.personendaten.anrede)
        changedApplicantNames.anrede = newLead.personendaten.anrede;
    if (oldLead.personendaten.vorname !== newLead.personendaten.vorname)
        changedApplicantNames.vorname = newLead.personendaten.vorname;
    if (oldLead.personendaten.nachname !== newLead.personendaten.nachname)
        changedApplicantNames.nachname = newLead.personendaten.nachname;

    if (Object.keys(changedApplicantNames).length > 0) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantName,
            payload: {
                applicantId,
                ...changedApplicantNames,
            },
        });
    }

    if (oldLead.personendaten.email !== newLead.personendaten.email) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmail,
            payload: {
                applicantId,
                email: newLead.personendaten.email,
            },
        });
    }

    if (oldLead.personendaten.telefonPrivat !== newLead.personendaten.telefonPrivat) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.changeApplicantPhone,
            payload: {
                applicantId,
                telefonPrivat: newLead.personendaten.telefonPrivat,
            },
        });
    }

    if (
        oldLead.personendaten.geburtsdatum?.toString() !== newLead.personendaten.geburtsdatum?.toString() ||
        oldLead.personendaten.geburtsland !== newLead.personendaten.geburtsland ||
        oldLead.personendaten.geburtsort !== newLead.personendaten.geburtsort ||
        oldLead.personendaten.geburtsname !== newLead.personendaten.geburtsname
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.updateApplicantBirthData,
            payload: {
                applicantId,
                geburtsdatum: newLead.personendaten.geburtsdatum,
                geburtsland: newLead.personendaten.geburtsland,
                geburtsname: newLead.personendaten.geburtsname,
                geburtsort: newLead.personendaten.geburtsort,
            },
        });
    }

    if (
        oldLead.personendaten.staatsangehoerigkeit !== newLead.personendaten.staatsangehoerigkeit ||
        /* eslint-disable eqeqeq */
        oldLead.personendaten.aufenthaltstitel != newLead.personendaten.aufenthaltstitel ||
        oldLead.personendaten.inDeutschlandSeit?.toString() != newLead.personendaten.inDeutschlandSeit?.toString() ||
        oldLead.personendaten.aufenthaltBefristetBis?.toString() !=
            newLead.personendaten.aufenthaltBefristetBis?.toString()
        /* eslint-enable eqeqeq */
    ) {
        commands.push({
            commandName: LOAN_REQUEST_COMMANDS.updateApplicantResidenceData,
            payload: {
                applicantId,
                aufenthaltBefristetBis: newLead.personendaten.aufenthaltBefristetBis ?? null,
                aufenthaltstitel: newLead.personendaten.aufenthaltstitel ?? null,
                inDeutschlandSeit: newLead.personendaten.inDeutschlandSeit ?? null,
                staatsangehoerigkeit: newLead.personendaten.staatsangehoerigkeit ?? null,
            },
        });
    }

    return commands;
}
